import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  GraduationCap,
  Users,
  MapPin,
  FileText,
  Loader2,
  AlertCircle,
  Trophy,
  School,
  CheckCircle2,
  Search,
  X,
} from "lucide-react";

import { useScholarshipDetail } from "@/services/scholarshipService";
import type { RootState } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import BackToTop from "@/components/common/BackToTop";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ScholarshipDetail: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const { id } = useParams<{ id: string }>();

  const {
    data: scholarship,
    isLoading,
    error,
  } = useScholarshipDetail(Number(id));

  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [isAllSponsorsOpen, setIsAllSponsorsOpen] = useState(false);
  const [sponsorSearch, setSponsorSearch] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    if (scholarship?.sessions && scholarship.sessions.length > 0) {
      const activeSession = scholarship.sessions.find((s) => s.is_active);
      if (activeSession) {
        setSelectedSessionId(activeSession.id || scholarship.sessions[0].id || null);
      } else {
        setSelectedSessionId(scholarship.sessions[0].id || null);
      }
    }
  }, [scholarship]);

  const activeSessionData = scholarship?.sessions?.find((s) => s.id === selectedSessionId);
  const mainSponsor = activeSessionData?.sponsors?.find((s) => s.is_main) || activeSessionData?.sponsors?.[0];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "inactive":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "expired":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-emerald-600 dark:text-emerald-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t("common.loading")}
            </p>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  if (error || !scholarship) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t("scholarships.notFound")}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t("scholarships.notFoundDescription")}
            </p>
            <Link to={`/${currentLanguage}/scholarships`}>
              <Button className="bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white">
                {t("scholarships.backToScholarships")}
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-600 via-stone-700 to-emerald-800 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Link
                to={`/${currentLanguage}/scholarships`}
                className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-all hover:-translate-x-1"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">{t("scholarships.backToScholarships")}</span>
              </Link>

              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
                <div className="flex-1 space-y-6">
                  <div className="flex items-center gap-4">
                    <Badge className={`${getStatusColor(scholarship.status)} border-0 text-xs px-3 py-1 shadow-sm`}>
                      {t(`scholarships.status.${scholarship.status}`).toUpperCase()}
                    </Badge>
                  </div>
                  <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
                    {scholarship.title}
                  </h1>
                  {mainSponsor && (
                    <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 w-fit">
                      {mainSponsor.picture && (
                        <img
                          src={mainSponsor.picture}
                          alt={mainSponsor.name}
                          className="w-16 h-16 rounded-xl object-cover border-2 border-white/30"
                        />
                      )}
                      <div>
                        <p className="text-white/70 text-sm font-medium uppercase tracking-wider">{t("scholarships.providedBy")}</p>
                        <h3 className="text-xl font-bold">{mainSponsor.name}</h3>
                        {mainSponsor.title && <p className="text-white/80 text-sm italic">{mainSponsor.title}</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 mb-20">
          {/* Year Selection Tabs */}
          {scholarship.sessions && scholarship.sessions.length > 0 && (
            <Tabs
              value={selectedSessionId?.toString() || ""}
              onValueChange={(val) => setSelectedSessionId(Number(val))}
              className="w-full mb-10"
            >
              <TabsList className="bg-white dark:bg-gray-800 p-1 rounded-2xl shadow-xl h-auto flex-wrap justify-start gap-2 border border-gray-200 dark:border-gray-700">
                {scholarship.sessions.map((session) => (
                  <TabsTrigger
                    key={session.id}
                    value={session.id?.toString() || ""}
                    className="rounded-xl data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-6 py-3 font-bold transition-all"
                  >
                    {session.academic_year}
                    {session.is_active && (
                      <span className="ml-2 w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
                {/* Main Content Column */}
                <div className="lg:col-span-2 space-y-12">
                  {/* About the Scholarship */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="space-y-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg">
                        <FileText className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">
                        {t("scholarships.about")}
                      </h2>
                    </div>

                    <Card className="bg-white dark:bg-gray-800 border-0 shadow-2xl rounded-3xl overflow-hidden group">
                      <CardContent className="p-8">
                        <div
                          className="prose dark:prose-invert max-w-none text-lg text-gray-700 dark:text-gray-300 leading-relaxed font-medium mb-8"
                          dangerouslySetInnerHTML={{ __html: scholarship.description || t("scholarships.noDescription") }}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-gray-100 dark:border-gray-700">
                          {scholarship.aim && (
                            <div className="space-y-2">
                              <h4 className="text-sm font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">{t("scholarships.ourAim")}</h4>
                              <div
                                className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 italic"
                                dangerouslySetInnerHTML={{ __html: scholarship.aim }}
                              />
                            </div>
                          )}
                          {scholarship.offers && (
                            <div className="space-y-2">
                              <h4 className="text-sm font-black text-stone-600 dark:text-stone-400 uppercase tracking-widest">{t("scholarships.weOffer")}</h4>
                              <div
                                className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 italic"
                                dangerouslySetInnerHTML={{ __html: scholarship.offers }}
                              />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Eligibility Section */}
                  {scholarship.eligibility && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      className="space-y-6"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-stone-100 dark:bg-stone-900/40 rounded-lg">
                          <CheckCircle2 className="w-6 h-6 text-stone-600 dark:text-stone-400" />
                        </div>
                        <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">
                          {t("scholarships.eligibility")}
                        </h2>
                      </div>
                      <div className="bg-gradient-to-br from-gray-900 to-stone-800 p-8 rounded-3xl text-white shadow-2xl">
                        <div
                          className="prose dark:prose-invert prose-emerald max-w-none text-lg leading-relaxed text-gray-200"
                          dangerouslySetInnerHTML={{ __html: scholarship.eligibility }}
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Beneficiaries for Selected Year */}
                  {activeSessionData?.beneficiaries && activeSessionData.beneficiaries.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="space-y-8"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">
                            {t("scholarships.beneficiaries")} ({activeSessionData.academic_year})
                          </h2>
                        </div>
                        <Badge className="bg-blue-600 text-white border-0 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                          {activeSessionData.beneficiaries.length} {t("scholarships.students")}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {activeSessionData.beneficiaries.map((beneficiary, index) => (
                          <Card key={index} className="bg-white dark:bg-gray-800 border-0 shadow-lg rounded-3xl hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                            <CardContent className="p-6 flex items-start gap-4">
                              <div className="relative flex-shrink-0">
                                <img
                                  src={beneficiary.picture || "https://ui-avatars.com/api/?name=" + encodeURIComponent(beneficiary.name) + "&background=random"}
                                  alt={beneficiary.name}
                                  className="w-20 h-20 rounded-2xl object-cover ring-4 ring-gray-50 dark:ring-gray-900 transition-transform group-hover:scale-105"
                                />
                                <div className="absolute -bottom-2 -right-2 bg-white dark:bg-gray-900 p-1 rounded-lg border border-gray-100 dark:border-gray-800">
                                  <GraduationCap className="w-4 h-4 text-emerald-600" />
                                </div>
                              </div>
                              <div className="space-y-2 flex-1">
                                <h4 className="text-xl font-black text-gray-900 dark:text-white leading-tight">{beneficiary.name}</h4>
                                <div className="space-y-1">
                                  {beneficiary.school && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                      <School className="w-4 h-4 text-emerald-500" />
                                      <span className="font-medium">{beneficiary.school}</span>
                                    </div>
                                  )}
                                  {beneficiary.location && (
                                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-500">
                                      <MapPin className="w-4 h-4 text-stone-400" />
                                      <span>{beneficiary.location}</span>
                                    </div>
                                  )}
                                  {beneficiary.class && (
                                    <div className="mt-2 inline-block px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px] font-black uppercase text-gray-500">
                                      {beneficiary.class}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Gallery Section */}
                  {activeSessionData?.gallery && activeSessionData.gallery.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="space-y-8"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                          <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">
                          {t("scholarships.gallery", "Gallery")} ({activeSessionData.academic_year})
                        </h2>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {activeSessionData.gallery.map((imageUrl, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ scale: 1.05 }}
                            onClick={() => setSelectedImage(imageUrl)}
                            className="relative aspect-square rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                          >
                            <img
                              src={imageUrl}
                              alt={`Gallery image ${index + 1}`}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                              <span className="text-white text-sm font-bold">View Image</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Sidebar Column */}
                <div className="space-y-8">
                  {/* Sponsors Section */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg">
                          <Trophy className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">
                          {t("scholarships.sponsors")}
                        </h2>
                      </div>
                      {activeSessionData?.sponsors && activeSessionData.sponsors.length > 12 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs font-bold text-amber-600 hover:text-amber-700"
                          onClick={() => setIsAllSponsorsOpen(true)}
                        >
                          {t("common.viewAll")}
                        </Button>
                      )}
                    </div>

                    {/* Main Sponsor Card (Featured) */}
                    {activeSessionData?.sponsors?.filter(s => s.is_main).map((sponsor, sIndex) => (
                      <Card key={`main-${sIndex}`} className="bg-white dark:bg-gray-800 border-2 border-emerald-500/50 shadow-2xl rounded-3xl overflow-hidden relative group">
                        <div className="absolute top-0 right-0 bg-emerald-600 text-white text-[10px] font-black px-3 py-1 rounded-bl-xl z-10 shadow-lg">
                          MAIN SPONSOR
                        </div>
                        <CardContent className="p-6 text-center space-y-4">
                          {sponsor.picture && (
                            <div className="relative inline-block">
                              <img
                                src={sponsor.picture}
                                alt={sponsor.name}
                                className="w-32 h-32 rounded-3xl object-cover mx-auto shadow-2xl border-4 border-gray-50 dark:border-gray-900 transform transition-transform group-hover:rotate-3"
                              />
                            </div>
                          )}
                          <div>
                            <h4 className="text-xl font-black text-gray-900 dark:text-white">{sponsor.name}</h4>
                            {sponsor.title && <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mt-1">{sponsor.title}</p>}
                          </div>
                          {sponsor.bio && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-900/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                              "{sponsor.bio}"
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}

                    {/* Co-Sponsors Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-1 gap-4">
                      {activeSessionData?.sponsors?.filter(s => !s.is_main).slice(0, 12).map((sponsor, sIndex) => (
                        <Card key={`co-${sIndex}`} className="bg-white dark:bg-gray-800 border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-all border border-gray-100 dark:border-gray-800/50 group">
                          <CardContent className="p-4 flex items-center lg:items-start lg:flex-row flex-col gap-3">
                            {sponsor.picture ? (
                              <img
                                src={sponsor.picture}
                                alt={sponsor.name}
                                className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl object-cover shadow-md border-2 border-gray-50 dark:border-gray-900 transition-transform group-hover:scale-110"
                              />
                            ) : (
                              <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                                <Trophy className="w-6 h-6 text-amber-500" />
                              </div>
                            )}
                            <div className="text-center lg:text-left min-w-0 flex-1">
                              <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate" title={sponsor.name}>{sponsor.name}</h4>
                              {sponsor.title && <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider truncate" title={sponsor.title}>{sponsor.title}</p>}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {activeSessionData?.sponsors && activeSessionData.sponsors.length > 12 && (
                      <Button
                        variant="outline"
                        className="w-full rounded-2xl h-12 border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-amber-500 hover:text-amber-500 transition-all font-bold"
                        onClick={() => setIsAllSponsorsOpen(true)}
                      >
                        + {activeSessionData.sponsors.length - 12} {t("scholarships.moreSponsors")}
                      </Button>
                    )}
                  </div>

                  {/* Summary Info */}
                  <Card className="bg-gradient-to-br from-emerald-600 to-stone-700 border-0 shadow-2xl rounded-3xl text-white overflow-hidden">
                    <CardHeader className="bg-white/5 border-b border-white/10">
                      <CardTitle className="flex items-center gap-2 text-md uppercase font-black">
                        <GraduationCap className="w-5 h-5 text-emerald-300" />
                        {t("scholarships.programStats")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6">
                      <div className="flex justify-between items-center bg-black/10 p-3 rounded-2xl border border-white/5">
                        <span className="text-white/70 text-sm font-bold uppercase">{activeSessionData?.academic_year}</span>
                        <Badge className="bg-white text-emerald-900 hover:bg-white">{t("scholarships.currentSelection")}</Badge>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between group">
                          <span className="text-sm text-white/70">{t("scholarships.studentsAwarded")}</span>
                          <span className="text-2xl font-black group-hover:scale-110 transition-transform">
                            {activeSessionData?.beneficiaries?.length || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between group">
                          <span className="text-sm text-white/70">{t("scholarships.partnerCount")}</span>
                          <span className="text-2xl font-black group-hover:scale-110 transition-transform">
                            {activeSessionData?.sponsors?.length || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between group border-t border-white/10 pt-4">
                          <span className="text-sm text-white/70 uppercase font-black tracking-widest">{t("scholarships.totalYears")}</span>
                          <span className="text-2xl font-black">
                            {scholarship.sessions?.length || 0}
                          </span>
                        </div>
                      </div>

                      <Link to={`/${currentLanguage}/scholarships`} className="block">
                        <Button className="w-full bg-white text-emerald-900 hover:bg-emerald-50 font-black rounded-2xl h-12 shadow-xl">
                          {t("scholarships.browseOthers")}
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </Tabs>
          )}

          {(!scholarship.sessions || scholarship.sessions.length === 0) && (
            <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700">
              <AlertCircle className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium italic">{t("scholarships.noSessionsAvailable")}</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />

      {/* View All Sponsors Dialog */}
      <Dialog open={isAllSponsorsOpen} onOpenChange={setIsAllSponsorsOpen}>
        <DialogContent className="max-w-4xl bg-white dark:bg-gray-800 border-0 shadow-2xl p-0 overflow-hidden rounded-3xl">
          <DialogHeader className="p-8 bg-gradient-to-r from-amber-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-3xl font-black uppercase tracking-tighter flex items-center gap-3">
                <Trophy className="w-8 h-8" />
                {t("scholarships.sponsors")} ({activeSessionData?.academic_year})
              </DialogTitle>
            </div>
            <div className="mt-6 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
              <Input
                placeholder={t("common.search")}
                value={sponsorSearch}
                onChange={(e) => setSponsorSearch(e.target.value)}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-14 rounded-2xl focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
          </DialogHeader>

          <div className="p-8 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {activeSessionData?.sponsors
                ?.filter(s => s.name.toLowerCase().includes(sponsorSearch.toLowerCase()))
                .map((sponsor, sIndex) => (
                  <div key={sIndex} className="flex items-center gap-4 p-4 rounded-3xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 hover:border-amber-500 transition-all group">
                    {sponsor.picture ? (
                      <img
                        src={sponsor.picture}
                        alt={sponsor.name}
                        className="w-16 h-16 rounded-2xl object-cover shadow-lg group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                        <Trophy className="w-8 h-8 text-amber-600" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h4 className="text-lg font-black text-gray-900 dark:text-white truncate">{sponsor.name}</h4>
                      {sponsor.title && <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest truncate">{sponsor.title}</p>}
                      {sponsor.is_main && <Badge className="mt-2 bg-emerald-600 text-white border-0 text-[8px] px-2 py-0">MAIN</Badge>}
                    </div>
                  </div>
                ))}
            </div>
            {activeSessionData?.sponsors?.filter(s => s.name.toLowerCase().includes(sponsorSearch.toLowerCase())).length === 0 && (
              <div className="text-center py-20 opacity-50">
                <Search className="w-12 h-12 mx-auto mb-4" />
                <p className="font-bold">{t("common.noResults")}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl w-full bg-transparent border-0 shadow-none p-0 flex items-center justify-center focus:outline-none">
          {selectedImage && (
            <div className="relative w-full h-full flex items-center justify-center animate-in fade-in zoom-in duration-300">
              <Button
                variant="ghost"
                size="icon"
                className="absolute -top-12 right-0 text-white hover:bg-white/20 rounded-full z-50 transition-colors"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-8 h-8" />
                <span className="sr-only">Close</span>
              </Button>
              <img
                src={selectedImage}
                alt="Gallery View"
                className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl ring-1 ring-white/10"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

ScholarshipDetail.displayName = "ScholarshipDetail";

export default ScholarshipDetail;
